import { SuggestionRowItem } from 'lib-knowledge-management-common/components/suggestionRow/suggestionRow';
import {
  AiDraftListItemFragment,
  LanguageId,
} from 'lib-knowledge-management-common/graphql';
import { toSummary } from 'lib-knowledge-management-common/utils/toSummary';

import { CategoryNameLookup } from '../hooks/useCategoryNames';

/** A suggestion's row, plus what its preview and actions need. */
export type Suggestion = SuggestionRowItem & {
  id: string;
  /** Full draft body as HTML. The list clamps `summary`; the preview renders this. */
  htmlContent: string;
  languageId: LanguageId;
  collectionId: string | undefined;
  categoryId: string | undefined;
  /**
   * The collections this draft may be filed into. Empty means unconstrained —
   * the queue it came from is linked to nothing, or it records no queue.
   */
  possibleCollectionIds: readonly string[];
  forArticleId: string | undefined;
  /** Being rebased onto a just-published article, so it cannot be approved. */
  isLocked: boolean;
};

export function toSuggestion(
  draft: AiDraftListItemFragment,
  collectionNamesById: ReadonlyMap<string, string>,
  categoryName: CategoryNameLookup,
): Suggestion {
  return {
    id: draft.id,
    type: draft.type,
    internalOnly: draft.internalOnly,
    title: draft.title,
    htmlContent: draft.htmlContent,
    languageId: draft.languageId,
    // The AI does not always write a summary, and the opening of the body is
    // the next best description of the draft. `trim()` rather than `??` because
    // whitespace reads as an empty row, and `NonEmptyString` does not rule it
    // out.
    summary: draft.summary?.trim() || toSummary(draft.htmlContent),
    collectionId: draft.collectionId ?? undefined,
    collectionName: draft.collectionId
      ? collectionNamesById.get(draft.collectionId)
      : undefined,
    categoryId: draft.categoryId ?? undefined,
    categoryName: categoryName(draft.categoryId ?? undefined, draft.languageId),
    possibleCollectionIds: draft.possibleCollectionIds,
    // `referencedConversationIds` is documented as the *other* conversations, so
    // the one that triggered the draft has to be added — but it does sometimes
    // appear among them too, and listing it twice would both duplicate a row and
    // overstate the count.
    conversations: [
      { csid: draft.initiatedFromConversation, isInitiating: true },
      ...draft.referencedConversationIds
        .filter((csid) => csid !== draft.initiatedFromConversation)
        .map((csid) => ({ csid })),
    ],
    initiatedBy: draft.initiatedBy?.name
      ? {
          name: draft.initiatedBy.name,
          email: draft.initiatedBy.email ?? undefined,
          avatarUrl: draft.initiatedBy.avatarUrl ?? undefined,
        }
      : undefined,
    activityAt: draft.modifiedAt ?? draft.createdAt,
    forArticleId: draft.forArticleId ?? undefined,
    isLocked: draft.isLocked,
  };
}
